import { create } from 'zustand';
import { enqueueSnackbar, closeSnackbar, SnackbarKey } from 'notistack';

type Severity = 'success' | 'error' | 'warning' | 'info';

interface NotificationStore {
  show: (message: string, severity: Severity, duration?: number) => SnackbarKey;
  showSuccess: (message: string, duration?: number) => SnackbarKey;
  showError: (message: string, duration?: number) => SnackbarKey;
  showWarning: (message: string, duration?: number) => SnackbarKey;
  showInfo: (message: string, duration?: number) => SnackbarKey;
  dismiss: (id: SnackbarKey) => void;
}

const DEFAULT_DURATIONS: Record<Severity, number> = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000,
};

function toast(message: string, severity: Severity, duration?: number): SnackbarKey {
  const text = normalizeMessage(message);
  return enqueueSnackbar(text, {
    variant: severity,
    autoHideDuration: duration ?? DEFAULT_DURATIONS[severity],
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
  });
}

function normalizeMessage(message: unknown): string {
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) {
    return message
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : JSON.stringify(d)))
      .join('; ');
  }
  try {
    return String(message);
  } catch {
    return 'Something happened.';
  }
}

export const useNotificationStore = create<NotificationStore>()(() => ({
  show: (message, severity, duration) => toast(message, severity, duration),
  showSuccess: (message, duration) => toast(message, 'success', duration),
  showError: (message, duration) => toast(message, 'error', duration),
  showWarning: (message, duration) => toast(message, 'warning', duration),
  showInfo: (message, duration) => toast(message, 'info', duration),
  dismiss: (id) => closeSnackbar(id),
}));
